// src/routes/checklist/ChecklistPage.tsx
import React from 'react';
import { 
  Container, 
  Title, 
  Paper, 
  Stack,
  Checkbox,
  Text,
  Group,
  Button,
  Divider
} from '@mantine/core';
import { IconPlus } from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface ChecklistItem {
  id: number;
  title: string;
  description: string;
  isCompleted: boolean;
  completedAt?: string;
  completedByUserId?: number;
  requestId?: number;
}

export function ChecklistPage() {
  const queryClient = useQueryClient();
  
  const { data: items, isLoading } = useQuery({
    queryKey: ['checklist'],
    queryFn: async () => {
      const token = localStorage.getItem('okta-token-storage');
      const { data } = await axios.get(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/api/ChecklistItem`, {
        headers: {
          Authorization: `Bearer ${token ? JSON.parse(token).accessToken.accessToken : ''}`
        }
      });
      return data;
    }
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, isCompleted }: { id: number; isCompleted: boolean }) => {
      const token = localStorage.getItem('okta-token-storage');
      await axios.put(
        `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080'}/api/ChecklistItem/${id}`,
        { isCompleted: !isCompleted },
        {
          headers: {
            Authorization: `Bearer ${token ? JSON.parse(token).accessToken.accessToken : ''}`
          }
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklist'] });
    }
  });

  const handleToggle = (item: ChecklistItem) => {
    toggleMutation.mutate({ id: item.id, isCompleted: item.isCompleted });
  };

  if (isLoading) {
    return (
      <Container size="xl" py="lg">
        <Text>Caricamento...</Text>
      </Container>
    );
  }

  const completedItems = items?.filter((item: ChecklistItem) => item.isCompleted) || [];
  const pendingItems = items?.filter((item: ChecklistItem) => !item.isCompleted) || [];

  return (
    <Container size="xl" py="lg">
      <Stack gap="md">
        <Group justify="space-between">
          <Title order={2}>Checklist</Title>
          <Button leftSection={<IconPlus size={16} />}>
            Nuovo Item
          </Button>
        </Group>

        <Paper withBorder p="md">
          <Stack gap="md">
            <div>
              <Text fw={600} mb="sm">Da Completare ({pendingItems.length})</Text>
              <Stack gap="sm">
                {pendingItems.map((item: ChecklistItem) => (
                  <Paper key={item.id} withBorder p="sm">
                    <Group wrap="nowrap">
                      <Checkbox
                        checked={item.isCompleted}
                        onChange={() => handleToggle(item)}
                      />
                      <div style={{ flex: 1 }}>
                        <Text fw={500}>{item.title}</Text>
                        {item.description && (
                          <Text size="sm" c="dimmed">{item.description}</Text>
                        )}
                      </div>
                    </Group>
                  </Paper>
                ))}
              </Stack>
            </div>

            {completedItems.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text fw={600} mb="sm" c="dimmed">Completati ({completedItems.length})</Text>
                  <Stack gap="sm">
                    {completedItems.map((item: ChecklistItem) => (
                      <Paper key={item.id} withBorder p="sm" style={{ opacity: 0.6 }}>
                        <Group wrap="nowrap">
                          <Checkbox
                            checked={item.isCompleted}
                            onChange={() => handleToggle(item)}
                          />
                          <div style={{ flex: 1 }}>
                            <Text fw={500} td="line-through">{item.title}</Text>
                            {item.completedAt && (
                              <Text size="xs" c="dimmed">
                                Completato: {new Date(item.completedAt).toLocaleDateString()}
                              </Text>
                            )}
                          </div>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </div>
              </>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
